import { useMemo } from 'react';
import { filterVisibleColumns } from '../util/filter-columns';
import { useAppSelector } from './useAppSelector';
import { Column } from '../enum/Column';

/**
 * useColumns
 *
 * A custom React hook that manages the visibility and pinned state of a list of table columns.
 * Useful for building dynamic table UIs where users can show/hide or pin/unpin columns.
 *
 * ### Features:
 * - Initializes state with all columns set to `{ pinned: false, visible: true }`
 * - Returns separate arrays for pinned, unpinned, and visible columns based on current state
 *
 * @param columns - Array of column identifiers (keys or names)
 *
 * @returns An object containing:
 * - `pinnedColumns`: Array of column keys that are currently pinned
 * - `unpinnedColumns`: Array of column keys that are not pinned
 * - `visibleColumns`: Array of column keys that are currently visible
 */
export const useColumns = () => {
  const columns = useAppSelector((state) => state.columnOrder.columns);
  const visibleColumns = useMemo(() => filterVisibleColumns(columns), [columns]);

  // Separate locked (Geo / CCY) and draggable columns
  const lockedColumns = visibleColumns.filter((col) => col.isLocked);
  const draggableColumns = visibleColumns.filter((col) => !col.isLocked);

  const pinnedColumns = draggableColumns.filter((column: Column) => column.isPinned);
  const unpinnedColumns = draggableColumns.filter((column: Column) => !column.isPinned);

  return {
    pinnedColumns,
    unpinnedColumns,
    visibleColumns,
    lockedColumns,
    columns,
  };
};
